// src/components/Dashboard/PlayerDashboard/PlayerExtras/Chats.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BASE_URL } from '../../../../config';
import { apiFetch } from '../../../../apiFetch';
import io from 'socket.io-client';
import './Chats.css';

const Chats = () => {
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [partnerStatus, setPartnerStatus] = useState(null);
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth <= 768 : false
    );

    const [mobileListMode, setMobileListMode] = useState(
        typeof window !== 'undefined' ? window.innerWidth <= 768 : false
    );

    const socket = useRef(null);
    const prevConvIdRef = useRef(null);
    const messagesEndRef = useRef(null);
    const typingTimeout = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();

    // ✅ Player dashboard should use playerId from /me hydration
    const rawId =
        location.state?.playerId ??
        window.localStorage.getItem('playerId'); // <-- this is what your /me stores

    const parsedUserId = parseInt(rawId || 0, 10);


    /* -------------------- Helpers -------------------- */
    const sortConvs = (list) =>
        [...list].sort((a, b) => {
            const at = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : new Date(a.created_at).getTime();
            const bt = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : new Date(b.created_at).getTime();
            return bt - at; // most recent first
        });


    const isConversationLocked =
        activeConversation?.consentStatus === 'Completed' ||
        activeConversation?.locked === true;


    
    function getRoleProfilePath(role, slug) {
        if (!role || !slug) return null;

        switch (String(role).toLowerCase()) {
            case "agent":
                return `/agent-profile/${slug}`;
            case "scout":
                return `/scout-profile/${slug}`;
            case "club":
                return `/club-profile/${slug}`;
            default:
                return null;
        }
    }



    /* -------------------- Load conversations -------------------- */
    useEffect(() => {
        if (!parsedUserId) return;
        apiFetch(`/api/conversations/${parsedUserId}?role=player`)
            .then(res => res.json())
            .then(data => {
                // Include requests so pending chats are visible
                const visibleRequests = (data.requests || []).filter(
                    (c) =>
                        c.lastMessage && c.lastMessage.trim().length > 0
                );

                const all = [...(data.chats || []), ...visibleRequests];
                setConversations(sortConvs(all));

            })
            .catch(err => console.error('❌ Error loading conversations:', err));
    }, [parsedUserId]);



    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            setMobileListMode((prev) => {
                if (mobile && activeConversation == null) return true;
                if (!mobile) return false;
                return prev;
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [activeConversation]);




    // ⭐ Auto-select only on desktop
    useEffect(() => {
        if (isMobile || activeConversation || conversations.length === 0) return;

        const pendingForPlayer = conversations.find(
            (c) =>
                c.status === 'pending' &&
                c.participant2_id === parsedUserId &&
                c.lastMessage
        );

        setActiveConversation(pendingForPlayer || conversations[0]);
    }, [conversations, activeConversation, parsedUserId, isMobile]);



    /* -------------------- Socket setup -------------------- */
    useEffect(() => {
        socket.current = io(BASE_URL);
        if (parsedUserId) socket.current.emit('registerUser', { userId: parsedUserId });
        return () => socket.current?.disconnect();
    }, [parsedUserId]);

    // ✅ Join/leave conversation rooms (CONSENT SAFE)
    useEffect(() => {
        if (!socket.current) return;

        if (prevConvIdRef.current && prevConvIdRef.current !== activeConversation?.id) {
            socket.current.emit('leaveConversation', prevConvIdRef.current);
        }

        if (activeConversation?.id && !isConversationLocked) {
            socket.current.emit('joinConversation', activeConversation.id);
            prevConvIdRef.current = activeConversation.id;
        }

    }, [activeConversation?.id, isConversationLocked]);




    // ✅ Handle typing events
    useEffect(() => {
        if (!socket.current) return;
        const onTyping = ({ conversationId, senderId }) => {
            if (conversationId === activeConversation?.id && senderId !== parsedUserId) {
                setIsTyping(true);
            }
        };
        const onStopTyping = ({ conversationId, senderId }) => {
            if (conversationId === activeConversation?.id && senderId !== parsedUserId) {
                setIsTyping(false);
            }
        };
        socket.current.on('typing', onTyping);
        socket.current.on('stopTyping', onStopTyping);
        return () => {
            socket.current.off('typing', onTyping);
            socket.current.off('stopTyping', onStopTyping);
        };
    }, [activeConversation?.id, parsedUserId]);

    // ✅ Real-time: listen for new messages (update active thread + sidebar)
    useEffect(() => {
        if (!socket.current) return;

        const onNew = (msg) => {
            const isActive =
                String(msg.conversation_id) === String(activeConversation?.id);

            if (isActive) {
                // 🔒 Prevent duplicate messages
                setMessages((prev) => {
                    if (prev.some((m) => m.id === msg.id)) return prev;
                    return [...prev, msg];
                });

                // ⬇️ Auto-scroll
                setTimeout(() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                }, 0);
            }

            // 🔄 Always update sidebar metadata
            setConversations((list) =>
                sortConvs(
                    list.map((c) =>
                        String(c.id) === String(msg.conversation_id)
                            ? {
                                ...c,
                                lastMessage: msg.message,
                                lastMessageTime: msg.created_at,
                                unreadCount: isActive ? 0 : (c.unreadCount || 0) + 1
                            }
                            : c
                    )
                )
            );
        };

        socket.current.on('newMessage', onNew);

        return () => {
            socket.current.off('newMessage', onNew);
        };
    }, [activeConversation?.id]);


    // ✅ (Optional) listen for conversationStatus if you emit it from backend
    useEffect(() => {
        if (!socket.current) return;

        const onStatus = ({ conversationId, status }) => {
            setConversations((list) =>
                sortConvs(
                    list.map((c) =>
                        String(c.id) === String(conversationId)
                            ? { ...c, status }
                            : c
                    )
                )
            );

            if (String(activeConversation?.id) === String(conversationId)) {
                setActiveConversation((prev) =>
                    prev ? { ...prev, status } : prev
                );
            }
        };

        socket.current.on('conversationStatus', onStatus);

        return () => {
            socket.current.off('conversationStatus', onStatus);
        };
    }, [activeConversation?.id]);


    // 🔒 Listen for conversation lock (consent expired)
    useEffect(() => {
        if (!socket.current) return;

        const onConversationLocked = ({ conversationId }) => {
            if (String(activeConversation?.id) === String(conversationId)) {
                // 🔒 Leave room immediately
                socket.current.emit('leaveConversation', conversationId);
                prevConvIdRef.current = null;

                setConversations((prev) =>
                    prev.map((c) =>
                        String(c.id) === String(conversationId)
                            ? { ...c, consentStatus: 'Completed', locked: true }
                            : c
                    )
                );

                setActiveConversation((prev) =>
                    prev
                        ? { ...prev, consentStatus: 'Completed', locked: true }
                        : prev
                );
            }
        };

        socket.current.on('conversationLocked', onConversationLocked);

        return () => {
            socket.current.off('conversationLocked', onConversationLocked);
        };
    }, [activeConversation?.id]);


    useEffect(() => {
        if (isConversationLocked) {
            setIsTyping(false);
        }
    }, [isConversationLocked]);





    // ✅ Load messages + partner status + mark read
    useEffect(() => {
        if (!activeConversation?.id) return;

        // Mark as read for this user
        apiFetch(`/api/messages/mark-read/${activeConversation.id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: parsedUserId })
        })
            .then(() => {
                setConversations(prev =>
                    prev.map(c => (c.id === activeConversation.id ? { ...c, unreadCount: 0 } : c))
                );
            })
            .catch(err => console.error('❌ Error marking messages as read:', err));

        // Load messages
        apiFetch(`/api/messages/${activeConversation.id}`)
            .then(res => res.json())
            .then(setMessages)
            .catch(err => console.error('❌ Error loading messages:', err));

        // Partner status
        const partnerId =
            activeConversation.participant1_id === parsedUserId
                ? activeConversation.participant2_id
                : activeConversation.participant1_id;

        apiFetch(`/api/online-status/${partnerId}`)
            .then(res => res.json())
            .then(setPartnerStatus)
            .catch(err => console.error('❌ Error fetching status:', err));
    }, [activeConversation, parsedUserId]);

    // ⛔ No local append — let socket 'newMessage' render it to avoid duplicates
    const handleSend = async () => {
        if (!newMessage.trim() || !activeConversation) return;
        const payload = {
            conversationId: activeConversation.id,
            senderId: parsedUserId,
            message: newMessage.trim(),
        };
        try {
            const res = await apiFetch(`/api/messages/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('send failed');
            setNewMessage('');
        } catch (err) {
            console.error('❌ Error sending message:', err);
        }
    };


    const handleSelectConversation = (conv) => {
        setMobileListMode(false);
        setActiveConversation(conv);
    };



    /* ========= Pending approval (Accept/Decline) ========= */
    const showApprovalUI =
        activeConversation?.status === 'pending' &&
        activeConversation?.participant2_id === parsedUserId;

    const handleAccept = async () => {
        if (!activeConversation?.id) return;
        try {
            const res = await apiFetch(`/api/conversations/accept`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    conversationId: activeConversation.id,
                    userId: parsedUserId
                })
            });
            if (!res.ok) throw new Error('Failed to accept');
            setActiveConversation(prev => ({ ...prev, status: 'accepted' }));
            setConversations(list =>
                sortConvs(list.map(c => (c.id === activeConversation.id ? { ...c, status: 'accepted' } : c)))
            );
        } catch (err) {
            console.error('❌ Accept failed:', err);
            alert('Could not accept. Try again.');
        }
    };

    const handleDecline = async () => {
        if (!activeConversation?.id) return;
        try {
            const res = await apiFetch(`/api/conversations/decline`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    conversationId: activeConversation.id,
                    userId: parsedUserId
                })
            });
            if (!res.ok) throw new Error('Failed to decline');

            setConversations(list => list.filter(c => c.id !== activeConversation.id));
            setActiveConversation(null);
            setMobileListMode(true);
        } catch (err) {
            console.error('❌ Decline failed:', err);
            alert('Could not decline. Try again.');
        }
    };


     

    const showChatList = !isMobile || mobileListMode || !activeConversation;
    const showChatWindow = !isMobile || (!mobileListMode && !!activeConversation);




    return (
        <div className="player-chats inbox-modal">
            <div className="inbox-container">
                {showChatList && (
                    <div className="chat-list">
                        <div className="chat-header">
                            <h3>Chats</h3>
                        </div>
                        {conversations.map((conv) => (
                            <div
                                key={conv.id}
                                className={`chat-preview ${activeConversation?.id === conv.id ? 'active' : ''}`}
                                onClick={() => handleSelectConversation(conv)}
                            >
                                <div className="preview-left">
                                    <strong>{conv.partnerName}</strong>
                                    <div className="preview-snippet">
                                        {conv.lastMessage?.slice(0, 40) || 'No messages yet'}...
                                    </div>
                                </div>
                                {conv.unreadCount > 0 && (
                                    <div className="unread-badge">{conv.unreadCount}</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {showChatWindow && (
                    <div className="chat-window">
                        {activeConversation ? (
                            <>
                                {isMobile && (
                                    <div className="mobile-chat-topbar">
                                        <button
                                            className="mobile-back-btn"
                                            onClick={() => {
                                                setMobileListMode(true);
                                                setActiveConversation(null);
                                            }}
                                        >
                                            ← Back
                                        </button>
                                    </div>
                                )}

                                <div
                                    className="chat-partner-header"
                                    onClick={() => {
                                        const { partnerRole, partnerSlug } = activeConversation;

                                        const path = getRoleProfilePath(partnerRole, partnerSlug);

                                        if (!path) {
                                            console.error('❌ Missing partner role or slug:', activeConversation);
                                            return;
                                        }

                                        navigate(path, {
                                            state: {
                                                viewerRole: 'player',
                                                from: 'chat',
                                            },
                                        });
                                    }}
                                >
                                    <strong>{activeConversation.partnerName}</strong>
                                    <div className="online-status">
                                        {partnerStatus?.is_online ? (
                                            <span className="online-dot">● Online</span>
                                        ) : partnerStatus?.last_seen ? (
                                                <span>
                                                    Last seen {new Date(partnerStatus.last_seen).toLocaleTimeString(undefined, {
                                                        hour: 'numeric',
                                                        minute: '2-digit',
                                                        hour12: true
                                                    })}
                                                </span>
                                        ) : null}
                                    </div>
                                </div>

                                <div className="chat-messages">
                                    {messages.map((msg) => (
                                        <div
                                            key={msg.id}
                                            className={`chat-message ${parseInt(msg.sender_id, 10) === parsedUserId ? 'sent' : 'received'}`}
                                        >
                                            <div className="message-text">{msg.message}</div>
                                            <div className="message-time">
                                                {new Date(msg.created_at).toLocaleTimeString(undefined, {
                                                    hour: 'numeric',
                                                    minute: '2-digit',
                                                    hour12: true
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                    {isTyping && <div className="typing-indicator">Typing...</div>}
                                    <div ref={messagesEndRef} />
                                </div>

                                {showApprovalUI ? (
                                    <div className="pending-approval">
                                        <div className="approval-card">
                                            <div className="approval-avatar">
                                                {(activeConversation?.partnerName || '?').charAt(0)}
                                            </div>

                                            <div className="approval-copy">
                                                <div className="approval-title">
                                                    <strong>{activeConversation?.partnerName}</strong> wants to start a chat.
                                                </div>
                                                <div className="approval-sub">
                                                    You’ll be able to reply once you accept.
                                                </div>
                                            </div>

                                            <div className="approval-actions">
                                                <button className="btn btn-accept" onClick={handleAccept}>Accept</button>
                                                <button className="btn btn-decline" onClick={handleDecline}>Decline</button>
                                            </div>
                                        </div>
                                    </div>
                                ) : isConversationLocked ? (
                                    <div className="chat-locked-banner">
                                        🔒 Messaging with this user has been locked because parental consent has expired.
                                    </div>
                                ) : (
                                    <div className="chat-input">
                                        <input
                                            type="text"
                                            placeholder="Type a message..."
                                            value={newMessage}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setNewMessage(value);

                                                if (
                                                    !activeConversation ||
                                                    activeConversation.status !== 'accepted' ||
                                                    !socket.current?.connected
                                                ) {
                                                    return;
                                                }

                                                socket.current.emit('typing', {
                                                    conversationId: activeConversation.id,
                                                    senderId: parsedUserId,
                                                });

                                                clearTimeout(typingTimeout.current);
                                                typingTimeout.current = setTimeout(() => {
                                                    socket.current.emit('stopTyping', {
                                                        conversationId: activeConversation.id,
                                                        senderId: parsedUserId,
                                                    });
                                                }, 3000);
                                            }}
                                            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                            disabled={activeConversation?.status !== 'accepted' || isConversationLocked}
                                        />
                                        <button
                                            onClick={handleSend}
                                            disabled={activeConversation?.status !== 'accepted' || isConversationLocked}
                                        >
                                            Send
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="no-chat-selected">
                                Only Agents, Scouts, and Clubs can initiate chat. Once they message you, chats will appear here.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Chats;







